import React, { useEffect, useState, useMemo } from "react";
import {
    CircularProgress,
    Button,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Avatar,
    Paper,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon           from "@mui/icons-material/Search";
import FilterListIcon       from "@mui/icons-material/FilterList";
import ClearIcon            from "@mui/icons-material/Clear";
import AddIcon              from "@mui/icons-material/Add";
import SchoolIcon           from "@mui/icons-material/School";
import ArrowUpwardIcon      from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon    from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon       from "@mui/icons-material/UnfoldMore";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import QuizIcon from "@mui/icons-material/Quiz";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import * as XLSX from "xlsx";
import { get_all_schools_api, act_as_admin_api } from "../../DAL/FlightSchools/FlightSchools";
import { useGodMode } from "../../Hooks/GodModeContext";
import { baseUrl } from "../../config/config";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#ffffff",
        borderRadius: "9px",
        color: "#1a1a1a",
        fontSize: "14px",
        "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
        "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
        "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { color: "#1a1a1a" },
    "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

const selectSx = {
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    color: "#1a1a1a",
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#c01e2e", borderWidth: "1.5px" },
    "& .MuiSvgIcon-root": { color: "#c01e2e" },
};

export default function FlightSchoolsList() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { enterGodMode } = useGodMode();
    const [isLoading, setIsLoading] = useState(true);
    const [schoolsList, setSchoolsList] = useState([]);

    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sort, setSort] = useState({ field: "last_active_at", dir: "desc" });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        getFlightSchools();
    }, []);

    const getFlightSchools = async () => {
        try {
            const result = await get_all_schools_api();
            if (result.code === 200 || result.schools) {
                setSchoolsList(result.schools || []);
                setIsLoading(false);
            } else {
                enqueueSnackbar(result.message || "Failed to load flight schools", { variant: "error" });
                setIsLoading(false);
            }
        } catch (error) {
            enqueueSnackbar("Error fetching flight schools", { variant: "error" });
            setIsLoading(false);
        }
    };

    const SCHOOL_SORT = {
        name:                     (s) => (s.name || "").toLowerCase(),
        primary_admin_email:      (s) => (s.primary_admin_email || "").toLowerCase(),
        active_students_count:    (s) => s.active_students_count || 0,
        staff_count:              (s) => s.staff_count || 0,
        total_questions_generated:(s) => s.total_questions_generated || 0,
        last_active_at:           (s) => new Date(s.last_active_at || 0).getTime(),
        monthly_student_fee:      (s) => s.monthly_student_fee || 0,
        status:                   (s) => s.status || "",
    };

    const filteredData = useMemo(() => {
        const filtered = schoolsList.filter((school) => {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
                searchQuery === "" ||
                school.name?.toLowerCase().includes(searchLower) ||
                school.primary_admin_email?.toLowerCase().includes(searchLower);
            const matchesStatus = statusFilter === "all" || school.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
        if (!sort.field) return filtered;
        const getter = SCHOOL_SORT[sort.field];
        return [...filtered].sort((a, b) => {
            const av = getter(a), bv = getter(b);
            if (av < bv) return sort.dir === "asc" ? -1 : 1;
            if (av > bv) return sort.dir === "asc" ? 1 : -1;
            return 0;
        });
    }, [schoolsList, searchQuery, statusFilter, sort]);

    const hasActiveFilters = searchQuery !== "" || statusFilter !== "all";

    const handleSort = (field) => {
        setPage(0);
        setSort((prev) =>
            prev.field === field
                ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
                : { field, dir: field === "last_active_at" ? "desc" : "asc" }
        );
    };

    const clearFilters = () => { setSearchQuery(""); setStatusFilter("all"); };
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

    const handleViewSchool = (school) => navigate(`/flight-schools/${school._id}`);
    const handleEditSchool = (school) => navigate(`/flight-schools/edit/${school._id}`);

    const handleActAsAdmin = async (school) => {
        try {
            const result = await act_as_admin_api(school._id);
            if (result.code === 200) {
                enterGodMode(school);
                navigate("/school/dashboard");
            } else {
                enqueueSnackbar(result.message || "Failed to enter admin mode", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Error entering admin mode", { variant: "error" });
        }
    };

    const exportToExcel = () => {
        const exportData = filteredData.map((school) => ({
            Name: school.name,
            "Primary Admin Email": school.primary_admin_email,
            "Included Seats": school.included_seats,
            "Active Students": school.active_students_count || 0,
            Staff: school.staff_count || 0,
            "Questions Generated": school.total_questions_generated || 0,
            "Monthly Student Fee": `£${school.monthly_student_fee || 0}`,
            Status: school.status,
            "Last Active": school.last_active_at ? moment(school.last_active_at).format("DD-MM-YYYY HH:mm") : "Never",
            "Created At": school.createdAt ? moment(school.createdAt).format("DD-MM-YYYY HH:mm") : "",
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Flight Schools");
        XLSX.writeFile(workbook, "flight_schools_list.xlsx");
    };

    const getStatusChipSx = (status) => {
        switch (status) {
            case "active":    return { backgroundColor: "rgba(22,163,74,0.1)", color: "#16a34a" };
            case "suspended": return { backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706" };
            case "expired":   return { backgroundColor: "rgba(192,30,46,0.1)", color: "#c01e2e" };
            default:          return { backgroundColor: "rgba(26,26,26,0.06)", color: "#6b6b6b" };
        }
    };

    return (
        <>
            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                    <CircularProgress sx={{ color: "#c01e2e" }} />
                </Box>
            ) : (
                <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "calc(100vh - 48px)" }}>
                    <Paper sx={{
                        backgroundColor: "#ffffff",
                        border: "1px solid rgba(26,26,26,0.09)",
                        borderRadius: "14px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                        p: 3,
                    }}>
                        {/* Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pb: 2.5, borderBottom: "1px solid rgba(26,26,26,0.09)" }}>
                            <Box>
                                <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>
                                    Flight Schools
                                </Typography>
                                <Typography sx={{ fontSize: "13px", color: "#aaa", fontFamily: "'Rubik', sans-serif", mt: 0.25 }}>
                                    {schoolsList.length} partner schools
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    onClick={exportToExcel}
                                    sx={{
                                        borderColor: "rgba(26,26,26,0.2)",
                                        color: "#6b6b6b",
                                        textTransform: "none",
                                        fontFamily: "'Rubik', sans-serif",
                                        fontSize: "13px",
                                        borderRadius: "8px",
                                        "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.06)" },
                                    }}
                                >
                                    Export
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => navigate("/flight-schools/add")}
                                    sx={{
                                        backgroundColor: "#c01e2e",
                                        color: "#fff",
                                        textTransform: "none",
                                        fontFamily: "'Rubik', sans-serif",
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        borderRadius: "8px",
                                        px: 2.5,
                                        boxShadow: "none",
                                        "&:hover": { backgroundColor: "#a81828", boxShadow: "none" },
                                    }}
                                >
                                    Add School
                                </Button>
                            </Box>
                        </Box>

                        {/* Filters */}
                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Search by name or email…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ ...inputSx, minWidth: 260 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: "#aaa", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel sx={{ color: "#aaa", fontSize: "14px", "&.Mui-focused": { color: "#c01e2e" } }}>Status</InputLabel>
                                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={selectSx}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="suspended">Suspended</MenuItem>
                                    <MenuItem value="expired">Expired</MenuItem>
                                </Select>
                            </FormControl>

                            {hasActiveFilters && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={clearFilters}
                                    startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        borderColor: "rgba(26,26,26,0.2)",
                                        color: "#6b6b6b",
                                        textTransform: "none",
                                        fontFamily: "'Rubik', sans-serif",
                                        fontSize: "13px",
                                        borderRadius: "8px",
                                        "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.06)" },
                                    }}
                                >
                                    Clear
                                </Button>
                            )}

                            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.75 }}>
                                <FilterListIcon sx={{ color: "#aaa", fontSize: 16 }} />
                                <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                    {filteredData.length} of {schoolsList.length}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Active filter chips */}
                        {hasActiveFilters && (
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                                {searchQuery && (
                                    <Chip label={`Search: "${searchQuery}"`} onDelete={() => setSearchQuery("")} size="small"
                                        sx={{ backgroundColor: "rgba(192,30,46,0.08)", color: "#c01e2e", fontFamily: "'Rubik', sans-serif", fontSize: "12px",
                                            "& .MuiChip-deleteIcon": { color: "#c01e2e" } }} />
                                )}
                                {statusFilter !== "all" && (
                                    <Chip label={`Status: ${statusFilter}`} onDelete={() => setStatusFilter("all")} size="small"
                                        sx={{ backgroundColor: "rgba(192,30,46,0.08)", color: "#c01e2e", fontFamily: "'Rubik', sans-serif", fontSize: "12px",
                                            "& .MuiChip-deleteIcon": { color: "#c01e2e" } }} />
                                )}
                            </Box>
                        )}

                        {/* Table */}
                        <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {/* Plain header helper */}
                                        {[{ plain: "#", width: 48 }].map(({ plain, width }) => (
                                            <TableCell key={plain} sx={{ color: "#6b6b6b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif", borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4", width }}>
                                                {plain}
                                            </TableCell>
                                        ))}
                                        {/* Sortable headers */}
                                        {[
                                            { label: "School",      field: "name" },
                                            { label: "Admin Email", field: "primary_admin_email" },
                                            { label: "Seats",       field: "active_students_count" },
                                            { label: "Staff",       field: "staff_count" },
                                            { label: "Questions",   field: "total_questions_generated" },
                                            { label: "Last Active", field: "last_active_at" },
                                            { label: "Fee",         field: "monthly_student_fee" },
                                            { label: "Status",      field: "status" },
                                        ].map(({ label, field }) => {
                                            const active = sort.field === field;
                                            return (
                                                <TableCell key={field} onClick={() => handleSort(field)} sx={{
                                                    color: active ? "#c01e2e" : "#6b6b6b", fontWeight: 600, fontSize: "11px",
                                                    textTransform: "uppercase", letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif",
                                                    borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4",
                                                    cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                                                    "&:hover": { color: "#c01e2e", "& .sort-icon": { opacity: 1 } },
                                                }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {label}
                                                        <Box className="sort-icon" sx={{
                                                            display: "flex", flexDirection: "column", alignItems: "center",
                                                            ml: 0.25, opacity: active ? 1 : 0.5, transition: "opacity 0.15s ease",
                                                        }}>
                                                            <ArrowUpwardIcon   sx={{ fontSize: 9, display: "block", mb: "-2px", color: active && sort.dir === "asc"  ? "#c01e2e" : "inherit" }} />
                                                            <ArrowDownwardIcon sx={{ fontSize: 9, display: "block",             color: active && sort.dir === "desc" ? "#c01e2e" : "inherit" }} />
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell sx={{ color: "#6b6b6b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif", borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4", width: 120 }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((school, index) => (
                                            <TableRow
                                                key={school._id || index}
                                                hover
                                                onClick={() => handleViewSchool(school)}
                                                sx={{
                                                    cursor: "pointer",
                                                    "&:hover": { backgroundColor: "#F7F4EF" },
                                                    "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" },
                                                }}
                                            >
                                                <TableCell sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                    {page * rowsPerPage + index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Avatar
                                                            src={school.logo ? baseUrl + school.logo : undefined}
                                                            sx={{ width: 38, height: 38, backgroundColor: school.primary_color || "#c01e2e", fontSize: "13px" }}
                                                        >
                                                            <SchoolIcon sx={{ fontSize: 18 }} />
                                                        </Avatar>
                                                        <Box>
                                                            <Box sx={{ color: "#1a1a1a", fontSize: "14px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>
                                                                {school.name}
                                                            </Box>
                                                            <Box sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif", display: "flex", alignItems: "center", gap: 0.5 }}>
                                                                <PeopleIcon sx={{ fontSize: 13 }} />
                                                                {school.active_students_count || 0} students
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                    {school.primary_admin_email}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                        {school.active_students_count || 0} / {school.included_seats}
                                                        {(school.active_students_count || 0) > school.included_seats && (
                                                            <Chip
                                                                label={`+${(school.active_students_count || 0) - school.included_seats} extra`}
                                                                size="small"
                                                                sx={{ ml: 1, backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706", fontSize: "10px", height: "20px", fontFamily: "'Rubik', sans-serif" }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                        <AdminPanelSettingsIcon sx={{ fontSize: 14, color: "#aaa" }} />
                                                        {school.staff_count || 0}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                        <QuizIcon sx={{ fontSize: 14, color: "#aaa" }} />
                                                        {(school.total_questions_generated || 0).toLocaleString()}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
                                                        <AccessTimeIcon sx={{ fontSize: 13 }} />
                                                        {school.last_active_at ? moment(school.last_active_at).fromNow() : "Never"}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                                                    {school.monthly_student_fee != null ? `£${school.monthly_student_fee}` : "—"}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={school.status || "active"}
                                                        size="small"
                                                        sx={{
                                                            ...getStatusChipSx(school.status),
                                                            borderRadius: "6px",
                                                            fontSize: "11px",
                                                            fontWeight: 500,
                                                            fontFamily: "'Rubik', sans-serif",
                                                            textTransform: "capitalize",
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", gap: 0.25 }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewSchool(school); }}
                                                                sx={{ color: "#aaa", "&:hover": { color: "#c01e2e" } }}>
                                                                <VisibilityIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit School">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditSchool(school); }}
                                                                sx={{ color: "#aaa", "&:hover": { color: "#c01e2e" } }}>
                                                                <EditIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Act as Admin (God Mode)">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleActAsAdmin(school); }}
                                                                sx={{ color: "#c01e2e", "&:hover": { backgroundColor: "rgba(192,30,46,0.08)" } }}>
                                                                <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={filteredData.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            sx={{
                                color: "#6b6b6b",
                                fontFamily: "'Rubik', sans-serif",
                                "& .MuiTablePagination-selectIcon": { color: "#c01e2e" },
                                "& .MuiTablePagination-actions button": { color: "#6b6b6b" },
                            }}
                        />
                    </Paper>
                </Box>
            )}
        </>
    );
}
